import React from 'react'
import { Radio, Input, Select } from '../../../shared/components'
import { Address } from '../../../shared/types/battery'

interface CheckoutStep1Props {
  deliveryMethod: 'shipping' | 'pickup' | null
  setDeliveryMethod: (method: 'shipping' | 'pickup' | null) => void
  shippingAddress: Address
  handleAddressChange: (field: keyof Address, value: string) => void
  formErrors: Record<string, string>
  setFormErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
  isLoadingAddresses: boolean
  userAddresses: Array<{
    id?: string | null
    name: string
    street: string
    number: string
    complement?: string
    neighborhood: string
    city: string
    state: string
    zipCode: string
    isDefault?: boolean
  }>
  selectedAddressId: string | null
  setSelectedAddressId: (id: string | null) => void
  showNewAddressForm: boolean
  setShowNewAddressForm: (show: boolean) => void
  setShippingAddress: React.Dispatch<React.SetStateAction<Address>>
  addressConfirmed: boolean
  setAddressConfirmed: (confirmed: boolean) => void
  selectedShippingOption: string | null
  setSelectedShippingOption: (option: string | null) => void
  setSelectedShipping: (shipping: string) => void
  isLoadingCEP: boolean
  cepError: string | null
  setCepError: (error: string | null) => void
  handleCEPBlur: (value: string) => void
  loadAddressToForm: (address: any) => Promise<void>
  shippingOptions: Array<{
    method: string
    name: string
    price: number
    estimatedDays: number
    description?: string
  }>
  isLoadingShipping: boolean
  calculateShippingOptions: () => Promise<void>
  validateStep1: () => boolean
  setCurrentStep: (step: number) => void
  formatPrice: (price: number) => string
  handles: Record<string, string>
  storeAddress: Address
  saveNewAddress: () => Promise<boolean>
}

export const CheckoutStep1: React.FC<CheckoutStep1Props> = ({
  deliveryMethod,
  setDeliveryMethod,
  shippingAddress,
  handleAddressChange,
  formErrors,
  setFormErrors,
  isLoadingAddresses,
  userAddresses,
  selectedAddressId,
  setSelectedAddressId,
  showNewAddressForm,
  setShowNewAddressForm,
  setShippingAddress,
  addressConfirmed,
  setAddressConfirmed,
  selectedShippingOption,
  setSelectedShippingOption,
  setSelectedShipping,
  isLoadingCEP,
  cepError,
  setCepError,
  handleCEPBlur,
  loadAddressToForm,
  shippingOptions,
  isLoadingShipping,
  calculateShippingOptions,
  validateStep1,
  setCurrentStep,
  formatPrice,
  handles,
  storeAddress,
  saveNewAddress
}) => {
  const formatZipCode = (value: string): string => {
    const cleanValue = value.replace(/\D/g, '')
    let formatted = cleanValue

    if (cleanValue.length > 8) {
      formatted = cleanValue.slice(0, 8)
    }

    if (formatted.length > 5) {
      formatted = formatted.replace(/(\d{5})(\d{0,3})/, '$1-$2')
    }

    return formatted
  }

  return (
    <div className={`${handles.formSection}`}>
      <h2 className={`${handles.formTitle} text-xl font-semibold text-gray-900 mb-6`}>
        Como você prefere receber seu pedido?
      </h2>

      {!deliveryMethod ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => setDeliveryMethod('shipping')}
            className="flex flex-col items-center justify-center p-8 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
          >
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
              </svg>
            </div>
            <span className="text-lg font-semibold text-gray-900 mb-1">Entrega</span>
            <span className="text-sm text-gray-500 text-center">Entrega rápida e segura no seu endereço</span>
          </button>

          <button
            onClick={() => {
              setDeliveryMethod('pickup')
              setSelectedShippingOption('pickup')
              setSelectedShipping('pickup')
              setAddressConfirmed(true)
            }}
            className="flex flex-col items-center justify-center p-8 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-all group"
          >
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <span className="text-lg font-semibold text-gray-900 mb-1">Retirada na Loja</span>
            <span className="text-sm text-gray-500 text-center">Av. José de Sousa Campos, 2086 - Nova Campinas, Campinas - SP</span>
            <span className="text-sm text-gray-500 text-center">CEP: 13025-320</span>
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {deliveryMethod === 'pickup' ? 'Endereço de Faturamento' : 'Endereço de Entrega'}
            </h3>
            <button
              onClick={() => {
                setDeliveryMethod(null)
                setAddressConfirmed(false)
                setSelectedShippingOption(null)
              }}
              className="text-sm text-blue-600 hover:text-blue-700 underline"
            >
              Alterar opção
            </button>
          </div>

          {deliveryMethod === 'pickup' && (
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-6">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-blue-800 font-medium mb-1">Por que precisamos do seu endereço?</p>
                  <p className="text-sm text-blue-700">
                    Mesmo para retirada na loja, precisamos do seu endereço para emissão da Nota Fiscal e validação de segurança do pagamento.
                  </p>
                </div>
              </div>
            </div>
          )}

          {deliveryMethod === 'pickup' && (
            <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mb-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 text-lg mb-1">Local de Retirada: BaterPower - Loja Campinas</h4>
                  <p className="text-gray-600 mb-1">
                    {storeAddress.street}, {storeAddress.number}
                    {storeAddress.complement && ` - ${storeAddress.complement}`}
                  </p>
                  <p className="text-gray-600 mb-3">
                    {storeAddress.neighborhood} - {storeAddress.city}/{storeAddress.state}
                    <br />
                    CEP: {storeAddress.zipCode}
                  </p>
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Disponível para retirada em 2 horas
                  </div>
                </div>
              </div>
            </div>
          )}

          {isLoadingAddresses ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Carregando endereços...</p>
            </div>
          ) : userAddresses.length === 0 && !showNewAddressForm ? (
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 bg-gray-50 text-center">
              <p className="text-gray-600 mb-6">
                Você ainda não cadastrou nenhum endereço.
              </p>
              <button
                type="button"
                onClick={() => {
                  setShowNewAddressForm(true)
                  setSelectedAddressId(null)
                  setShippingAddress({
                    street: '',
                    number: '',
                    complement: '',
                    neighborhood: '',
                    city: '',
                    state: '',
                    zipCode: '',
                    country: 'Brasil'
                  })
                  setAddressConfirmed(false)
                }}
                className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-sm"
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Cadastrar novo endereço
              </button>
            </div>
          ) : userAddresses.length > 0 && !showNewAddressForm ? (
            <div className="space-y-4">
              <div className="mb-4">
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Selecione um endereço de entrega
                </h3>
                <div className="space-y-4">
                  {userAddresses.map((address) => (
                    <label
                      key={address.id}
                      className={`relative flex items-start p-6 border rounded-xl cursor-pointer transition-all duration-200 group ${selectedAddressId === address.id
                          ? 'border-blue-500 bg-blue-50 shadow-sm'
                          : 'border-gray-200 bg-gray-50 hover:border-blue-300 hover:bg-blue-50/50'
                        }`}
                    >
                      <div className="flex items-start w-full">
                        <div className="mr-4 mt-1">
                          <Radio
                            name="address"
                            value={address.id || ''}
                            checked={selectedAddressId === address.id}
                            onChange={() => {
                              setSelectedAddressId(address.id || null)
                              void loadAddressToForm(address)
                              setShowNewAddressForm(false)
                            }}
                          />
                        </div>

                        <div className="hidden sm:flex w-12 h-12 bg-blue-100 rounded-full items-center justify-center flex-shrink-0 mr-4">
                          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        </div>

                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {address.name}
                            </h3>
                            {address.isDefault && (
                              <span className="bg-green-500 text-white px-2 py-0.5 rounded-md text-xs font-semibold uppercase tracking-wide">
                                Padrão
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {address.street}, {address.number}
                            {address.complement ? `, ${address.complement}` : ''}
                            <br />
                            {address.neighborhood} - {address.city}/{address.state}
                            <br />
                            CEP: {address.zipCode}
                          </p>
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setShowNewAddressForm(true)
                  setSelectedAddressId(null)
                  setShippingAddress({
                    street: '',
                    number: '',
                    complement: '',
                    neighborhood: '',
                    city: '',
                    state: '',
                    zipCode: '',
                    country: 'Brasil'
                  })
                  setAddressConfirmed(false)
                }}
                className="w-full py-4 px-6 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 font-medium hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50/30 transition-all duration-200 flex items-center justify-center gap-2"
              >
                <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Adicionar novo endereço
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {userAddresses.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setShowNewAddressForm(false)
                    setSelectedAddressId(null)
                    setAddressConfirmed(false)
                  }}
                  className="mb-4 text-sm text-blue-600 hover:text-blue-700 underline"
                >
                  ← Voltar para escolher endereço
                </button>
              )}

              <div className={`${handles.formRow} grid grid-cols-1 md:grid-cols-2 gap-4`}>
                <div className={`${handles.formGroup}`}>
                  <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                    CEP *
                    {isLoadingCEP && (
                      <span className="ml-2 text-xs text-blue-600">Consultando...</span>
                    )}
                  </label>
                  <div className="relative">
                    <Input
                      type="text"
                      value={shippingAddress.zipCode}
                      onChange={(e) => {
                        const formatted = formatZipCode(e.target.value)
                        handleAddressChange('zipCode', formatted)
                        setCepError(null)
                        setFormErrors(prev => ({ ...prev, zipCode: '' }))
                      }}
                      onBlur={(e) => handleCEPBlur(e.target.value)}
                      placeholder="00000-000"
                      maxLength={9}
                      error={!!(cepError || formErrors.zipCode)}
                      helperText={(cepError ?? formErrors.zipCode) || undefined}
                    />
                    {isLoadingCEP && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className={`${handles.formRow} grid grid-cols-1 md:grid-cols-3 gap-4`}>
                <div className={`${handles.formGroup} md:col-span-2`}>
                  <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                    Rua *
                  </label>
                  <Input
                    type="text"
                    value={shippingAddress.street}
                    onChange={(e) => {
                      handleAddressChange('street', e.target.value)
                      setFormErrors(prev => ({ ...prev, street: '' }))
                    }}
                    error={!!formErrors.street}
                    helperText={formErrors.street}
                  />
                </div>
                <div className={`${handles.formGroup}`}>
                  <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                    Número *
                  </label>
                  <Input
                    type="text"
                    value={shippingAddress.number}
                    onChange={(e) => {
                      handleAddressChange('number', e.target.value)
                      setFormErrors(prev => ({ ...prev, number: '' }))
                    }}
                    error={!!formErrors.number}
                    helperText={formErrors.number}
                  />
                </div>
              </div>

              <div className={`${handles.formGroup}`}>
                <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                  Complemento
                </label>
                <Input
                  type="text"
                  value={shippingAddress.complement}
                  onChange={(e) => handleAddressChange('complement', e.target.value)}
                  placeholder="Apto, Bloco, etc..."
                />
              </div>

              <div className={`${handles.formGroup}`}>
                <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                  Bairro *
                </label>
                <Input
                  type="text"
                  value={shippingAddress.neighborhood}
                  onChange={(e) => {
                    handleAddressChange('neighborhood', e.target.value)
                    setFormErrors(prev => ({ ...prev, neighborhood: '' }))
                  }}
                  error={!!formErrors.neighborhood}
                  helperText={formErrors.neighborhood}
                />
              </div>

              <div className={`${handles.formRow} grid grid-cols-1 md:grid-cols-2 gap-4`}>
                <div className={`${handles.formGroup}`}>
                  <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                    Cidade *
                  </label>
                  <Input
                    type="text"
                    value={shippingAddress.city}
                    onChange={(e) => {
                      handleAddressChange('city', e.target.value)
                      setFormErrors(prev => ({ ...prev, city: '' }))
                    }}
                    error={!!formErrors.city}
                    helperText={formErrors.city}
                  />
                </div>
                <div className={`${handles.formGroup}`}>
                  <label className={`${handles.formLabel} block text-sm font-medium text-gray-700 mb-1`}>
                    Estado *
                  </label>
                  <Select
                    value={shippingAddress.state}
                    onChange={(value) => {
                      handleAddressChange('state', value)
                      setFormErrors(prev => ({ ...prev, state: '' }))
                    }}
                    options={[
                      { value: '', label: 'UF' },
                      ...['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'].map((uf) => ({
                        value: uf,
                        label: uf
                      }))
                    ]}
                    placeholder="UF"
                    className={`${handles.formSelect} ${formErrors.state ? 'border-red-500' : ''}`}
                  />
                  {formErrors.state && (
                    <p className="mt-1 text-xs text-red-600">{formErrors.state}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {addressConfirmed && (
            <div className={`${handles.shippingOptions} mt-6 border-t border-gray-200 pt-6`}>
              {deliveryMethod === 'shipping' && (
                <>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Opções de Entrega
                  </h3>

                  {isLoadingShipping ? (
                    <div className="text-center py-4">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                      <p className="text-sm text-gray-600">Calculando frete...</p>
                    </div>
                  ) : shippingOptions.length > 0 ? (
                    <div className="space-y-3">
                      {shippingOptions.map((option) => (
                        <label
                          key={option.method}
                          className={`${handles.shippingOption} flex items-center p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${selectedShippingOption === option.method
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-300'
                            }`}
                        >
                          <Radio
                            name="shipping"
                            value={option.method}
                            checked={selectedShippingOption === option.method}
                            onChange={(e) => {
                              setSelectedShippingOption(e.target.value)
                              setSelectedShipping(e.target.value)
                            }}
                            className="mr-3"
                          />
                          <div className="flex-1">
                            <div className="flex justify-between items-center mb-1">
                              <span className="font-medium text-gray-900">
                                {option.name}
                              </span>
                              <span className="font-semibold text-blue-600 text-lg">
                                {option.price === 0
                                  ? 'Grátis'
                                  : formatPrice(option.price)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">
                              {option.description || `Entrega em até ${option.estimatedDays} dias úteis`}
                            </p>
                          </div>
                        </label>
                      ))}
                    </div>
                  ) : shippingAddress.zipCode.replace(/\D/g, '').length === 8 ? (
                    <div className="text-center py-4 text-gray-600">
                      <p className="mb-2">Não foi possível calcular o frete para este endereço.</p>
                      <p className="text-sm text-gray-500 mb-3">
                        Verifique se o CEP está correto ou tente novamente.
                      </p>
                      <button
                        type="button"
                        onClick={() => void calculateShippingOptions()}
                        className="text-blue-600 hover:text-blue-700 underline text-sm font-medium"
                      >
                        Tentar novamente
                      </button>
                    </div>
                  ) : null}
                </>
              )}

              {addressConfirmed && (selectedShippingOption || deliveryMethod === 'pickup') && (
                <div className="mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      if (validateStep1()) {
                        if (deliveryMethod === 'pickup') {
                          // Se for pickup, definimos a opção de frete como pickup implicitamente
                          setSelectedShippingOption('pickup')
                          setSelectedShipping('pickup')
                        }
                        setCurrentStep(2)
                      }
                    }}
                    className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                  >
                    Avançar para Pagamento
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </div>
              )}
            </div>
          )}

          {!addressConfirmed && (
            <div className="mt-6">
              <button
                type="button"
                onClick={async () => {
                  if (validateStep1()) {
                    // Se for um novo endereço, salvar antes de confirmar
                    if (showNewAddressForm && !selectedAddressId) {
                      const saved = await saveNewAddress()
                      if (!saved) {
                        return
                      }
                    }
                    setAddressConfirmed(true)
                  }
                }}
                className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
              >
                Confirmar Endereço
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

